import { CARRY_OVER_RESTRICTED_LEGAL_ENTITIES, CARRY_OVER_UNAVAILABLE_DAYS } from '@/constants/order.constants';
import { ordersMixins } from '@/mixins/orders.mixins';
import { useUserStore } from '@/stores/user';
import { FabricDisabledReason } from '@/types/matrix.enums';
import { MatrixType, type LegalEntityEnum } from '@onrunning/backstage-models';
import { storeToRefs } from 'pinia';
import { computed, type Ref } from 'vue';
import { isDateAfter, isDateBefore, today } from '../utils/temporalUtils';

export const useFabricValidation = (matrixType: Ref<MatrixType | undefined>) => {
  const userStore = useUserStore();
  const { accountIsDistributor } = userStore;
  const { selectedAccount: account } = storeToRefs(userStore);

  const getFabricDisabledReason = (
    fabricLaunchDate: string | undefined,
    orderDate: string | Date | undefined,
    seasonStartDate: string | undefined,
  ): FabricDisabledReason | null => {
    if (!canDisableFabric.value) {
      return null;
    }

    if (isLaunchDateAfterSelectedDate(fabricLaunchDate, orderDate)) {
      return FabricDisabledReason.launchDateAfterOrderDate;
    }

    if (isCarryOverDisabled(fabricLaunchDate, orderDate, seasonStartDate)) {
      return FabricDisabledReason.carryOverRestricted;
    }

    return null;
  };

  const isLaunchDateAfterSelectedDate = (
    fabricLaunchDate: string | undefined,
    orderDate: string | Date | undefined,
  ): boolean => {
    if (!fabricLaunchDate) {
      return true;
    }

    return isDateAfter(fabricLaunchDate, orderDate ?? today());
  };

  const isLaunchDateBeforeSeasonStart = (
    fabricLaunchDate: string | undefined,
    orderDate: string | Date | undefined,
    seasonStartDate: string | undefined,
  ): boolean => {
    if (!fabricLaunchDate) {
      return true;
    }
    const isBeforeSeasonStart = isDateBefore(fabricLaunchDate, seasonStartDate ?? new Date());

    const isSelectedDateBeforeCarryOverStartDate = isDateBefore(orderDate ?? today(), seasonStartDate ?? today(), {
      days: CARRY_OVER_UNAVAILABLE_DAYS,
    });

    return isBeforeSeasonStart && isSelectedDateBeforeCarryOverStartDate;
  };

  const isCarryOverDisabled = (
    fabricLaunchDate: string | undefined,
    orderDate: string | Date | undefined,
    seasonStartDate: string | undefined,
  ): boolean => {
    if (!isCarryOverRestrictedLegalEntity.value) {
      return false;
    }

    return isLaunchDateBeforeSeasonStart(fabricLaunchDate, orderDate, seasonStartDate);
  };

  const canDisableFabric = computed((): boolean => {
    return ordersMixins.isSeasonalMatrixType(matrixType.value) && !accountIsDistributor();
  });

  const isCarryOverRestrictedLegalEntity = computed((): boolean => {
    return CARRY_OVER_RESTRICTED_LEGAL_ENTITIES.includes(account.value.legalEntity as LegalEntityEnum);
  });

  return {
    isCarryOverDisabled,
    getFabricDisabledReason,
    canDisableFabric,
    isCarryOverRestrictedLegalEntity,
  };
};
